import { ComponentType } from '@angular/cdk/overlay'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  AppointmentAuthorizationService,
  AppointmentDataService,
  AppointmentModel,
  AUTHORIZATION_STATUS,
  AuthorizationModel,
  Clinic,
  ClinicService,
  filterAction,
  INSURANCE_PROVIDER_SOURCE,
  INSURANCE_PROVIDER_TRANSACTION_TYPE,
  InsuranceProviderLookup,
  InsuranceProviderLookupsService,
  Patient,
  PATIENT_DATA_PROGRESS_KEY,
  PatientAccountModel,
  PatientDataService,
  PatientInsurance,
  PatientInsuranceStatus,
  Permission,
  WebsocketAction,
  WebSocketService,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import {
  combineLatestWith,
  concatMap,
  filter,
  finalize,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators'

import { AuthorizationDialogComponent, AuthorizationDialogData } from '../authorization-dialog'
import { PatientAuthHistoryDialogComponent } from '../patient-auth-history-dialog'
import { ProgressService } from '../progress'
import { ServiceReviewInquiryDialogComponent, ServiceReviewInquiryDialogData } from '../service-review-inquiry'
import { ServiceReviewSubmissionDialogComponent, ServiceReviewSubmissionDialogData, ServiceReviewSubmissionPreviewComponent } from '../service-review-submission'

@Component({
  selector: 'mmx-authorization-card',
  templateUrl: './authorization-card.component.html',
  standalone: false,
})
export class AuthorizationCardComponent implements OnInit, OnDestroy {
  private readonly onDestroy$ = new Subject<void>()

  public readonly Permission = Permission
  public readonly AUTHORIZATION_STATUS = AUTHORIZATION_STATUS
  public clinic: Clinic

  /**
   * We can pull Authorizations as long as:
   *
   * 1. There is a Auth Rule linked to an API
   * 2. The patient has insurances
   */
  public canPullPriorAuthorization$ = this.data.appointment$.pipe(
    takeUntil(this.onDestroy$),
    map(({ authRules }) => authRules?.find((authRule) => authRule.systemPortal?.api)),
  )

  public whyCantPullAuths$: Observable<{ text: string, tooltip?: string } | null> = of(null).pipe(
    takeUntil(this.onDestroy$),
    combineLatestWith(
      this.data.appointment$.pipe(
        takeUntil(this.onDestroy$),
        startWith(null),
      ),
      this.patientDataService.insurances$.pipe(
        takeUntil(this.onDestroy$),
        startWith(null),
      ),
    ),
    map(([, appointment, insurances]) => {
      if (!appointment) {
        return { text: this.translate.instant('patient-details.authorization-card.cant-pull.no-appointment') }
      } else if (!appointment.accountId) {
        return { text: this.translate.instant('patient-details.authorization-card.cant-pull.no-account') }
      } else if (!appointment.authRules?.length) {
        return { text: this.translate.instant('patient-details.authorization-card.cant-pull.no-auth-rule') }
      } else if (!appointment.authRules.find((au) => au.systemPortal?.api != null)) {
        return {
          text: appointment.authRules
            .filter((ar) => ar.systemPortal)
            .map((ar) => this.translate.instant('patient-details.authorization-card.cant-pull.no-api', { portalName: ar.systemPortal.name }))
            .join('\n'),
          tooltip: appointment.authRules
            .filter((ar) => ar.systemPortal)
            .map((ar) => ar.authorizationRequired
              ? this.translate.instant('patient-details.authorization-card.cant-pull.no-api-tooltip-required', { portalName: ar.systemPortal.name })
              : this.translate.instant('patient-details.authorization-card.cant-pull.no-api-tooltip-optional', { portalName: ar.systemPortal.name }),
            )
            .join('\n'),
        }
      } else if (!insurances?.length) {
        return { text: this.translate.instant('patient-details.authorization-card.cant-pull.no-insurance') }
      } else if (!insurances.find(insurance => insurance.status !== PatientInsuranceStatus.Inactive)) {
        return { text: this.translate.instant('patient-details.authorization-card.cant-pull.no-active-insurance') }
      } else {
        return null
      }
    }),
  )

  public tz: string
  public saving$ = new BehaviorSubject<boolean>(false)

  public loading$ = combineLatest([
    this.patientDataService.progressService.inProgress(PATIENT_DATA_PROGRESS_KEY.AUTHORIZATIONS),
    this.saving$,
  ])
    .pipe(
      map(([progress, saving]) => progress || saving),
    )

  public patient: Patient
  public appointment: AppointmentModel
  public hasPortals: boolean

  public allAuthorizations$ = this.patientDataService.authorizations$
    .pipe(
      combineLatestWith(this.data.appointment$),
      map(([authorizations, appointment]) => {
        const appointmentAuthorizations: AuthorizationModel[] = []
        const accountAuthorizations: AuthorizationModel[] = []
        const restAuthorizations: AuthorizationModel[] = []

        for (const authorization of authorizations) {
          if (authorization.appointmentIds?.has(appointment.id)) {
            appointmentAuthorizations.push(authorization)
          } else if (authorization.accountIds?.has(appointment.accountId)) {
            accountAuthorizations.push(authorization)
          } else {
            restAuthorizations.push(authorization)
          }
        }

        const sortByTypeAndDate = (a: AuthorizationModel, b: AuthorizationModel) => {
          const typeIndexA = a.patientInsurance?.typeIndex ?? 0
          const typeIndexB = b.patientInsurance?.typeIndex ?? 0

          if (typeIndexA !== typeIndexB) {
            return typeIndexA - typeIndexB
          }

          return a.createdAt?.diff(b.createdAt) || 0
        }

        return {
          appointmentAuthorizations: appointmentAuthorizations.sort(sortByTypeAndDate),
          accountAuthorizations: accountAuthorizations.sort(sortByTypeAndDate),
          restAuthorizations: restAuthorizations.sort(sortByTypeAndDate),
        }
      }),
      shareReplay(1),
    )

  public showAllAuthorizations$ = new BehaviorSubject<boolean>(false)
  public showFilteredAuthorizations$ = new BehaviorSubject<boolean>(false)

  public authorizations$ = combineLatest([
    this.showAllAuthorizations$,
    this.showFilteredAuthorizations$,
    this.allAuthorizations$,
  ])
    .pipe(
      map(([showAll, showFiltered, { appointmentAuthorizations, accountAuthorizations, restAuthorizations }]) => ([
        ...appointmentAuthorizations,
        ...accountAuthorizations,
        ...(showAll || showFiltered ? restAuthorizations : []),
      ])),
    )

  public numberOfHiddenAuths$ = combineLatest([
    this.allAuthorizations$,
    this.showAllAuthorizations$,
    this.showFilteredAuthorizations$,
  ])
    .pipe(
      map(([{ restAuthorizations }, showAll, showFiltered]) => showAll || showFiltered ? 0 : restAuthorizations.length),
    )

  constructor(
    private readonly dialog: MatDialog,
    public readonly clinicService: ClinicService,
    public readonly data: AppointmentDataService,
    public readonly patientDataService: PatientDataService,
    private readonly authorizationSvc: AppointmentAuthorizationService,
    private readonly translate: TranslateService,
    private readonly progressService: ProgressService,
    private readonly insuranceProviderLookupsService: InsuranceProviderLookupsService,
    private readonly websocketService: WebSocketService,
    private readonly snackBar: MatSnackBar,
  ) {
    this.tz = this.clinicService.clinic.settings.defaultTimezone ?? 'America/New_York'
  }

  ngOnInit(): void {
    this.websocketService.clinicianMessage$
      .pipe(
        takeUntil(this.onDestroy$),
        filterAction(WebsocketAction.PRIOR_AUTHORIZATION_STATUS),
      )
      .subscribe((response) => {
        if (response.error) {
          this.snackBar.open(response.message || this.translate.instant('prior-authorization.form.pull-status-fail'), null, { duration: 5000 })
          return
        }

        if (response.data?.authorization && response.data.authorization.patientId === this.patient.id) {
          if (response.message) {
            this.snackBar.open(response.message, null, { duration: 5000 })
          }
          this.refresh()
        }
      })

    this.clinic = this.clinicService.clinic

    this.data.appointment$.pipe(takeUntil(this.onDestroy$)).subscribe(appointment => {
      this.appointment = appointment
      this.hasPortals = appointment?.authRules?.some((rule) => !!rule.systemPortal)
    })

    this.patientDataService.patient$.pipe(takeUntil(this.onDestroy$)).subscribe(patient => {
      this.patient = patient
    })

    this.data.appointmentUpdated$
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => {
        this.patientDataService.refreshAuthorizations$.next()
      })
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  /**
   * Open dialog for add authorization
   */
  openAuthorizationDialog(authorization?: AuthorizationModel): void {
    this.patientDataService.patient$
      .pipe(
        takeUntil(this.onDestroy$),
        combineLatestWith(this.patientDataService.insurances$),
        first(),
        switchMap(([patient, patientInsurances]) => {
          const data: AuthorizationDialogData = {
            appointment: this.appointment,
            authorization,
            patient,
            patientInsurances,
            timezone: this.tz,
          }

          return this.dialog.open(AuthorizationDialogComponent, {
            width: '800px',
            data,
            panelClass: 'dialog-form',
            autoFocus: false,
          }).afterClosed()
        }),
        filter(Boolean),
        tap(() => this.refresh()),
      ).subscribe()
  }

  openPullServiceReviewsDialog(patient: Patient): void {
    const data: ServiceReviewInquiryDialogData = {
      patient,
      type: 'authorizations',
      appointment: this.appointment,
    }

    this.dialog.open(ServiceReviewInquiryDialogComponent, {
      width: '900px',
      panelClass: 'dialog-form',
      data,
    })
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
        tap(() => this.refresh()),
      )
      .subscribe()
  }

  deleteAuthorization(auth: AuthorizationModel): void {
    this.saving$.next(true)
    this.authorizationSvc.deleteAuthorization(auth.id).pipe(
      takeUntil(this.onDestroy$),
      tap(() => {
        this.refresh()
        this.saving$.next(false)
      }),
    ).subscribe()
  }

  private refresh() {
    this.patientDataService.refreshAuthorizations$.next()
    // the appointment's authorizationStatus may have changed, which powers the status icons
    this.data.appointmentUpdated$.next()
  }

  public save(authorization: AuthorizationModel) {
    this.saving$.next(true)
    this.authorizationSvc.updateAuthorization(authorization.id, authorization.toJSON()).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.saving$.next(false)),
      tap(() => {
        this.refresh()
        this.progressService.stop(this.translate.instant('patient-details.authorization-card.auths-saved'))
      }),
    ).subscribe()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isAuthSubmissionSupported(authorization: AuthorizationModel) {
    return false
  }

  private isCarrierSupported(lookup?: InsuranceProviderLookup): boolean {
    return (
      lookup?.provider?.source === INSURANCE_PROVIDER_SOURCE.AVAILITY &&
      lookup?.provider?.supportedTransactions?.includes(INSURANCE_PROVIDER_TRANSACTION_TYPE.AUTH_REF)
    )
  }

  openSubmitToPayerDialog(patient: Patient, authorization?: AuthorizationModel): void {
    this.getInsurance(authorization).pipe(
      takeUntil(this.onDestroy$),
      first(),
      concatMap(patientInsurance => this.insuranceProviderLookupsService.get(patientInsurance.carrierLookupId).pipe(
        map(providerLookup => [patientInsurance, providerLookup] as [PatientInsurance, InsuranceProviderLookup]),
      )),
      switchMap(([patientInsurance, providerLookup]) => {
        const data: ServiceReviewSubmissionDialogData = {
          appointment: this.appointment,
          patient,
          authorization,
          patientInsurance,
          providerLookup,
        }

        const dialogComponent: ComponentType<any> = this.isCarrierSupported(providerLookup) ?
          ServiceReviewSubmissionDialogComponent : ServiceReviewSubmissionPreviewComponent

        return this.dialog.open(dialogComponent, {
          width: '800px',
          data,
          panelClass: 'dialog-form',
        }).afterClosed()
      }),
      tap(() => this.refresh()),
    ).subscribe()
  }

  getInsurance(auth: AuthorizationModel) {
    return this.patientDataService.insurances$.pipe(
      takeUntil(this.onDestroy$),
      map(insurances => insurances?.find(({ id }) => id === auth.insuranceId)),
    )
  }

  public openAuthorizationHistoryDialog(account: PatientAccountModel) {
    this.dialog.open(PatientAuthHistoryDialogComponent, {
      data: {
        account,
      },
      panelClass: 'dialog-form',
    })
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
      )
      .subscribe((authorization: AuthorizationModel) => {
        this.openAuthorizationDialog(authorization)
      })
  }

  public pullAuthStatus(authorization: AuthorizationModel) {
    this.snackBar.open(this.translate.instant('prior-authorization.form.pull-status-intiated'), null, { duration: 5000 })

    this.websocketService.messageQueue.next({
      action: WebsocketAction.PRIOR_AUTHORIZATION_STATUS,
      authorizationId: authorization.id,
      appointmentId: this.appointment.id,
    })
  }

  public cancelPullAuthStatus(authorization: AuthorizationModel) {
    if (!authorization) {
      return
    }
    this.authorizationSvc.cancelPullAuthStatus(authorization.id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.refresh()
      })
  }
}
